package navigation.home.invoice.addeditinvoice

import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import cafe.adriel.voyager.core.screen.ScreenKey
import cafe.adriel.voyager.koin.koinScreenModel
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceEvent
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceNavigation
import com.essntl.features.invoice.ui.addeditinvoice.AddEditInvoiceScreen
import com.essntl.features.invoice.ui.addeditinvoice.components.content.extraservice.AddExtraServiceDialog
import com.essntl.features.service.ui.addextraservice.AddExtraServiceEvent
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import navigation.home.service.addextraservice.AddExtraServiceScreenModel
import navigation.utils.NavLinkScreen

class AddEditInvoiceScreenNav(
    private val proposalId: String,
    private val itineraryId: String? = null,
    private val invoiceId: String? = null,
) : NavLinkScreen {
    private val isEdit = invoiceId != null

    override val defaultTitle: String =
        if (isEdit)
            "Update Invoice"
        else
            "Create Invoice"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            isLoading = isEdit,
            isNavSidebarFixedByDefault = false,
            iconRes = EsIcons.Task,
            canRefresh = false,
        ),
    )

    override val key: ScreenKey = "AddEditInvoiceScreenNav/$invoiceId"

    @Composable
    override fun Content() {
        val addEditInvoiceScreenModel = koinScreenModel<AddEditInvoiceScreenModel>()
        val addEditInvoiceState by addEditInvoiceScreenModel.state.collectAsState()
        val validation = addEditInvoiceScreenModel.validation
        val onEvent = addEditInvoiceScreenModel::onEvent

        val addExtraServiceScreenModel = koinScreenModel<AddExtraServiceScreenModel>()
        val addExtraServiceState by addExtraServiceScreenModel.state.collectAsState()
        var isAddExtraServiceDialogOpened by rememberSaveable { mutableStateOf(false) }

        LaunchedEffect(addEditInvoiceState.getQuickbooksInvoice) {
            val title = addEditInvoiceState.getQuickbooksInvoice.data?.DocNumber

            if (isEdit) {
                navLinkState =
                    if (title != null) {
                        navLinkState.copy(
                            title = "Invoice $title",
                            isLoading = false,
                        )
                    } else {
                        navLinkState.copy(
                            isLoading = addEditInvoiceState.getQuickbooksInvoice.isLoading,
                        )
                    }
            }
        }

        LaunchedEffect(Unit) {
            validation.addEditInvoiceForm.reset()
            validation.addEditInvoiceForm.dueDate.value = addEditInvoiceState.dueDate

            onEvent(AddEditInvoiceEvent.GetInvoice(invoiceId))
            onEvent(AddEditInvoiceEvent.GetProposalItinerary(proposalId, itineraryId))
        }

        LaunchedEffect(
            addEditInvoiceState.getProposalServices.isLoading,
            addEditInvoiceState.getInvoiceProposalServices.isLoading,
            addEditInvoiceState.getItineraryServices.isLoading,
            addEditInvoiceState.getInvoiceItineraryServices.isLoading,
            addEditInvoiceState.extraServiceList.isLoading,
        ) {
            if (!addEditInvoiceState.getProposalServices.isLoading &&
                !addEditInvoiceState.getInvoiceProposalServices.isLoading &&
                !addEditInvoiceState.getItineraryServices.isLoading &&
                !addEditInvoiceState.getInvoiceItineraryServices.isLoading &&
                !addEditInvoiceState.extraServiceList.isLoading
            ) if (addEditInvoiceState.getAllProposalServices.isNotEmpty() ||
                addEditInvoiceState.getAllItineraryServices.isNotEmpty() ||
                addEditInvoiceState.extraServiceList.data
                    .orEmpty()
                    .isNotEmpty()
            ) {
                onEvent(AddEditInvoiceEvent.CheckInitialServices(addEditInvoiceState.getInvoice.data?.id))
            }
        }

        LaunchedEffect(Unit) {
            addExtraServiceScreenModel.validation.reset()
            addExtraServiceScreenModel.validation.price.value = addExtraServiceState.extraService.value.unitPrice ?: 0
            addExtraServiceScreenModel.validation.amount.value = addExtraServiceState.extraService.value.quantity ?: 1
            addExtraServiceScreenModel.validation.description.value =
                addExtraServiceState.extraService.value.description ?: ""
            addExtraServiceScreenModel.validation.name.value = addExtraServiceState.service.title

            addExtraServiceScreenModel.onEvent(AddExtraServiceEvent.LoadExtraCategoryServices)
        }

        LaunchedEffect(addExtraServiceState.addExtraService.isSuccess) {
            if (addExtraServiceState.addExtraService.isSuccess) {
                isAddExtraServiceDialogOpened = false
                addExtraServiceState.addExtraService.data?.let { es ->
                    val service = addExtraServiceState.service
                    onEvent(
                        AddEditInvoiceEvent.AddExtraService(
                            service = service,
                            extraService = es,
                        ),
                    )
                }
            }
        }

        AddEditInvoiceScreen(
            state = addEditInvoiceState,
            onEvent = onEvent,
            validation = validation,
            onNavigation = { event ->
                when (event) {
                    is AddEditInvoiceNavigation.AddExtraService -> {
                        isAddExtraServiceDialogOpened = true
                    }
                }
            },
        )

        AddExtraServiceDialog(
            isAddExtraServiceDialogOpened = isAddExtraServiceDialogOpened,
            state = addExtraServiceState,
            onEvent = addExtraServiceScreenModel::onEvent,
            validation = addExtraServiceScreenModel.validation,
            onDismissRequest = {
                if (!addExtraServiceState.addExtraService.isLoading)
                    isAddExtraServiceDialogOpened = false
            },
        )
    }
}